import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  useTheme,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import { changePasswordSchema } from '../../helpers/validations/changePasswordValidation';
import { AdminPasswordchange, EmployeePasswordchange, ParentPasswordchange, StudentPasswordchange } from '../../api/SchoolService/SchoolService';
import { ROUTES } from '../../constants/routes';
import { useSnackbar } from '../../components/MUIComponent/FeedbackComponent/SnackBar/SnackbarContext';
import Button from '../../components/Button/Button';
import '../../styles/layout/_mainLayout.scss';
import { useAuth } from '../../hooks/useAuth';
import { Roles } from '../../constants/params/constants';
import { useParentKidAuth } from '../../hooks/parentKidUseAuth';
import { handleLogout } from '../../services/logoutHandler';
import { useDispatch, useSelector } from 'react-redux';
import CustomeFormInputField from '../FormFields/CustomeFormInputField';
import { Controller } from "react-hook-form";
import { encryptPasswordAES } from '../../services/EncriptPasswordAES';
import * as Yup from "yup";
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';

const changePasswordSchemawithOtp = Yup.object().shape({
  oldPassword: Yup.string().required(() => i18n.t('validation.required')),
  newPassword: Yup
    .string()
    .required(() => i18n.t('validation.required'))
    .min(6, () => i18n.t('validation.passwordMustBeAtLeast6'))
    .max(50, () => i18n.t('validation.passwordMustBeAtMost50'))
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?#&^()_+=-]).*$/,
      () => i18n.t('validation.useUppercaseLowercaseNumberSymbol')
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], () => i18n.t('validation.passwordsMustMatch'))
    .required(() => i18n.t('validation.required')),
});

const ChangePasswordWithOtp = ({ setStep, setFormData, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [position] = useState(50);
  const { user } = useAuth();


  const { parentkidUser } = useParentKidAuth();
  const User =
    parentkidUser?.roleName === Roles[3].RoleName
      ? parentkidUser
      : user;
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
 
  const { showSnackbar } = useSnackbar();
  const currentLocation = useLocation();
  
  const { schoolProfileData } = useSelector(state => state.schoolProfile);


  const isEmployee = User?.roleName === Roles[2].RoleName || User?.roleName === Roles[4].RoleName || User?.roleName === Roles[7].RoleName ;
  const isParent = User?.roleName === Roles[3].RoleName;
  const isAdmin = User?.roleName === Roles[0].RoleName || User?.roleName === Roles[5].RoleName || User?.roleName === Roles[6].RoleName;
  const isStudent = User?.roleName === Roles[1].RoleName;

  const customColors = useSelector((state) => state.theme.customColors);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(changePasswordSchemawithOtp),
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleClickLogout = () => {
    handleLogout(dispatch, user, navigate, currentLocation, "manual", showSnackbar);
  };
 
  const getUserNameWithSchool = (registrationNumber) => {
    const shortName = schoolProfileData?.shortName || "";
    return shortName
      ? `${shortName}${registrationNumber}`
      : registrationNumber;
  };

  const onSubmit = async (formData) => {
    try {
      let response;
      const encryptedoldPassword = encryptPasswordAES(formData.oldPassword);
      const encryptednewPassword = encryptPasswordAES(formData.newPassword);
      
      if (isEmployee) {
        const payload = {
          userID: User?.employeeID,
          userName: User?.registrationNumber,

          oldPassword: encryptedoldPassword,
          newPassword: encryptednewPassword,
        };

        response = await EmployeePasswordchange(payload);

        if (response?.data?.success) {
           if(response?.data?.message === "Password has been reset successfully."){
            showSnackbar(response?.message || t('header.messages.passwordChangedSuccessfully'), "success");
            localStorage.setItem("token", response?.data?.object?.accessToken)
            localStorage.setItem("refreshToken", response?.data?.object?.refreshToken)
            onClose();
            return;
          }
          showSnackbar(response?.message || t('header.messages.otpSentSuccessfully'), "success");
          setFormData(formData);
          setStep(2);
          return;
        }
        else {
          showSnackbar(response?.message || t('header.messages.failedToSendOtp'), "error");
          return;
        }
      }

      if (isStudent) {
        const payload = {
          userID: User?.studentID,
          userName: User?.registrationNumber,

          oldPassword: encryptedoldPassword,
          newPassword: encryptednewPassword
        };

        response = await StudentPasswordchange(payload);

       if (response?.data?.success) {
           if(response?.data?.message === "Password has been reset successfully."){
            showSnackbar(response?.message || t('header.messages.passwordChangedSuccessfully'), "success");
            localStorage.setItem("token", response?.data?.object?.accessToken)
            localStorage.setItem("refreshToken", response?.data?.object?.refreshToken)
            onClose();
            return;
          }
          showSnackbar(response?.message || t('header.messages.otpSentSuccessfully'), "success");
          setFormData(formData);
          setStep(2);
          return;
        }

        showSnackbar(response?.message || t('header.messages.failedToSendOtp'), "error");
        return;
      }

      if (isParent) {
        const payload = {
          userID: User.parentID,
          userName: User.userName || User.email,
          oldPassword: encryptedoldPassword,
          newPassword: encryptednewPassword,
        };

        response = await ParentPasswordchange(payload);

        if (response?.data?.success) {
          if(response?.data?.message === "Password has been reset successfully."){
            showSnackbar(response?.message || t('header.messages.passwordChangedSuccessfully'), "success");
            localStorage.setItem("token", response?.data?.object?.accessToken)
            localStorage.setItem("refreshToken", response?.data?.object?.refreshToken)
            onClose();
            return;
          }
          showSnackbar(response?.message || t('header.messages.otpSentSuccessfully'), "success");
          setFormData(formData);
          setStep(2);
          return;
        }

        showSnackbar(response?.message || t('header.messages.failedToSendParentOtp'), "error");
        return;
      }


      // if (!user?.adminID || !user?.email) {
      //   showSnackbar("Admin details missing", "error");
      //   return;
      // }

      const payload = {
        userID: user.adminID,
        userName: user.email,
        oldPassword: encryptedoldPassword,
        newPassword: encryptednewPassword,
      };

      response = await AdminPasswordchange(payload);

      if (response?.data?.success) {
        showSnackbar(response?.message || t('header.messages.otpSentSuccessfully'), "success");
        setFormData(formData);
        setStep(2);
      } else {
        showSnackbar(response?.message || t('header.messages.failedToSendAdminOtp'), "error");
      }

    } catch (error) {
     
      showSnackbar(
        error?.response?.message || t('messages.somethingWentWrong'),
        "error"
      );
    }
  };

  const onFormError = (formErrors) => {
    const firstError = Object.values(formErrors)[0];
    if (firstError?.message) {
      showSnackbar(firstError.message, "error");
    }
  };

  return (
    <>
      <Box px={2} py={1}>
        <Box textAlign={"center"} mb={4}>
          <Typography variant="h6" mb={1} color={theme.palette.custom.TableheadText} className="card-title text-center">
            {t('common.changePassword')}
          </Typography>
          <Typography variant="body2" align="center" color={theme.palette.custom.TableheadText}>
            {t('header.enterYourOldPasswordAndCreate')}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box
            sx={{
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              height: "100%",
            }}
          >
            {/* Old Password */}
            <Controller
              name="oldPassword"
              control={control}
              render={({ field }) => (
                <CustomeFormInputField
                  {...field}
                  label={t('header.oldPassword')}
                  placeholder={t('header.enterOldPassword')}
                  type={showOld ? "text" : "password"}
                  error={!!errors.oldPassword}
                  helperText={errors.oldPassword?.message}
                  fullWidth
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowOld(!showOld)}>
                          {showOld ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />


            {/* New Password */}
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <CustomeFormInputField
                  {...field}
                  label={t('common.newPassword')}
                  placeholder={t('header.enterNewPassword')}
                  type={showNew ? "text" : "password"}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  fullWidth
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNew(!showNew)}>
                          {showNew ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />


            {/* Confirm Password */}
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <CustomeFormInputField
                  {...field}
                  label={t('common.confirmPassword')}
                  placeholder={t('header.enterConfirmPassword')}
                  type={showConfirm ? "text" : "password"}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  fullWidth
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)}>
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />


            {/* Button */}
            <Button
              variant="contained"
              fullWidth
              bgColor={theme.palette.background.bgpurple}
              textColor={theme.palette.custom.whitetext}
              padding="12px 10px"
              fontSize="16px"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? t('header.processing') :  User?.email === null ? t('common.submit') : t('header.verifyWithOtp') }
            </Button>


          </Box>
        </form>
      </Box>
    </>
  );
};

export default ChangePasswordWithOtp;